import { ChevronDown, ChevronUp, Plus, RotateCcw, X } from "lucide-react";
import { Button, Card } from "@/ui";
import { cn } from "@/lib/cn";
import {
  defaultFunnelSteps,
  MAX_FUNNEL_STEPS,
  MIN_FUNNEL_STEPS,
  normalizeDepth,
  serializeFunnelSteps,
  type FunnelStepDef,
  type FunnelStepKind,
} from "./funnelSteps";

/**
 * Editor for a property's funnel definition. Emits the full step list on every
 * change; the panel serializes it to the analytics-svc `?steps=` DSL and persists
 * it per property. Steps are ordered — drop-off is measured in sequence.
 */

const KIND_LABEL: Record<FunnelStepKind, string> = {
  pageview: "Pageview",
  click: "Click",
  event: "Event",
  scroll: "Scroll depth",
};

const inputCls =
  "h-8 rounded-btn border border-hairline bg-paper px-2 text-[13px] text-ink outline-none transition-shadow focus:ring-2 focus:ring-accent/40 dark:bg-white/5";

export function FunnelBuilder({
  steps,
  onChange,
}: {
  steps: FunnelStepDef[];
  onChange: (next: FunnelStepDef[]) => void;
}) {
  const setKind = (i: number, kind: FunnelStepKind) =>
    // Reset the value when the kind changes so an event name never leaks onto a scroll step.
    onChange(steps.map((s, idx) => (idx === i ? { kind } : s)));

  const setValue = (i: number, value: string) =>
    onChange(steps.map((s, idx) => (idx === i ? { ...s, value } : s)));

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = steps.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };

  const remove = (i: number) => {
    if (steps.length <= MIN_FUNNEL_STEPS) return;
    onChange(steps.filter((_, idx) => idx !== i));
  };

  const add = () => {
    if (steps.length >= MAX_FUNNEL_STEPS) return;
    onChange([...steps, { kind: "event" }]);
  };

  const isDefault =
    serializeFunnelSteps(steps) === serializeFunnelSteps(defaultFunnelSteps());

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">
            Funnel steps
          </h3>
          <p className="mt-0.5 text-[12px] text-subtle">
            Ordered — a visitor advances only by completing each step in sequence.
          </p>
        </div>
        <Button
          variant="quiet"
          size="sm"
          onClick={() => onChange(defaultFunnelSteps())}
          disabled={isDefault}
          title="Reset to the default view → interact → convert funnel"
        >
          <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} />
          Reset
        </Button>
      </div>

      <ol className="space-y-2">
        {steps.map((s, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-hairline font-mono text-[11px] text-muted">
              {i + 1}
            </span>

            <select
              value={s.kind}
              onChange={(e) => setKind(i, e.target.value as FunnelStepKind)}
              className={cn(inputCls, "w-[132px] cursor-pointer")}
              aria-label={`Step ${i + 1} type`}
            >
              {(Object.keys(KIND_LABEL) as FunnelStepKind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>

            {s.kind === "event" && (
              <input
                type="text"
                value={s.value ?? ""}
                onChange={(e) => setValue(i, e.target.value)}
                placeholder="event name (blank = any)"
                className={cn(inputCls, "min-w-0 flex-1")}
                aria-label={`Step ${i + 1} event name`}
              />
            )}

            {s.kind === "scroll" && (
              <span className="flex items-center gap-1.5">
                <input
                  type="number"
                  min={1}
                  max={100}
                  step={5}
                  value={Math.round(Number(normalizeDepth(s.value)) * 100)}
                  onChange={(e) => setValue(i, String(Number(e.target.value) / 100))}
                  className={cn(inputCls, "w-16")}
                  aria-label={`Step ${i + 1} scroll depth percent`}
                />
                <span className="text-[12px] text-muted">% depth</span>
              </span>
            )}

            <div className="ml-auto flex items-center gap-0.5">
              <IconBtn label="Move up" onClick={() => move(i, -1)} disabled={i === 0}>
                <ChevronUp className="h-4 w-4" strokeWidth={1.75} />
              </IconBtn>
              <IconBtn
                label="Move down"
                onClick={() => move(i, 1)}
                disabled={i === steps.length - 1}
              >
                <ChevronDown className="h-4 w-4" strokeWidth={1.75} />
              </IconBtn>
              <IconBtn
                label="Remove step"
                onClick={() => remove(i)}
                disabled={steps.length <= MIN_FUNNEL_STEPS}
              >
                <X className="h-4 w-4" strokeWidth={1.75} />
              </IconBtn>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-3 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={add} disabled={steps.length >= MAX_FUNNEL_STEPS}>
          <Plus className="h-3.5 w-3.5" strokeWidth={2} />
          Add step
        </Button>
        {steps.length >= MAX_FUNNEL_STEPS && (
          <span className="text-[11.5px] text-subtle">Max {MAX_FUNNEL_STEPS} steps</span>
        )}
      </div>
    </Card>
  );
}

function IconBtn({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="grid h-7 w-7 place-items-center rounded-btn text-muted transition-colors hover:bg-paper-2 hover:text-ink disabled:opacity-25 disabled:hover:bg-transparent dark:hover:bg-white/10"
    >
      {children}
    </button>
  );
}
